import { mapValues, zipObject } from 'es-toolkit';

import {
  ActiveSnowLoad,
  ComponentDesign,
  Exposure,
  LoadSettings,
  RiskCategory,
  TerrainExposureCategory,
  ThermalCondition,
} from '@paragon/contract-library';
import { Direction2D, equalWithinTolerance } from '@paragon/geometry-library';
import { calculateMemberDirectionedMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch, interpolate, notNullish } from '@paragon/utilities';

import { Asce7Version, getAsce7Version, isAsce7_22OrLater } from '../loadingStandards';

export function getMinimumTopChordPitch(componentDesign: ComponentDesign) {
  const topChords = componentDesign.component.members.filter((member) => member.memberType === 'TopChord');
  return Math.min(
    ...topChords
      .map((topChord) => calculateMemberDirectionedMidline(topChord, componentDesign.component))
      .filter(notNullish)
      .map((topChordMidline) => topChordMidline.direction.smallestAngleBetween(Direction2D.right)),
  );
}

export function calculateRoofSnowLoad(loadSettings: LoadSettings, componentDesign: ComponentDesign): number {
  const minimumTopChordPitch = getMinimumTopChordPitch(componentDesign);

  return p_s(loadSettings, minimumTopChordPitch);
}

/**
 * This is the client-side implementation of `SnowLoadSettings.p_s`
 */
function p_s(loadSettings: LoadSettings, pitch: number) {
  const asce7Version = getAsce7Version(loadSettings.buildingCode);

  const C_t = getThermalFactor(loadSettings);
  const C_s = getRoofSlopeFactor(
    pitch,
    C_t,
    loadSettings.slipperyRoof,
    loadSettings.isVentilated,
    loadSettings.rValue,
    asce7Version,
  );
  const p_f = convertSnowLoadFromSettings(loadSettings, 'Roof');
  return C_s * p_f;
}

export function determineGroundSnowLoadParameter(loadingStandard: Asce7Version | null) {
  return loadingStandard == null || !isAsce7_22OrLater(loadingStandard) ? 'AsdGround' : 'UltimateGround';
}

// Similar functionality is implemented server-side in computed properties GroundSnowLoad and p_f of SnowLoadSettings.
function convertSnowLoad(
  snowLoad: number,
  inputParameter: ActiveSnowLoad,
  outputParameter: ActiveSnowLoad,
  loadSettings: LoadSettings,
  thermalFactor?: number,
): number {
  if (inputParameter === outputParameter) return snowLoad;
  if (inputParameter === 'UltimateGround' && outputParameter === 'AsdGround') return 0.7 * snowLoad;
  if (inputParameter === 'AsdGround' && outputParameter === 'UltimateGround') return snowLoad / 0.7;

  const asce7Version = getAsce7Version(loadSettings.buildingCode);

  const C_e = getExposureFactor(loadSettings.terrainExposureCategory, loadSettings.exposure);
  const C_t = thermalFactor ?? getThermalFactor(loadSettings);
  const I_s = getImportanceFactor(loadSettings.riskCategory, asce7Version);

  const groundToRoofConversionFactor = 0.7 * C_e * C_t * I_s;

  const groundSnowLoadParameter = determineGroundSnowLoadParameter(asce7Version);
  if (inputParameter === 'Roof') {
    const groundSnowLoad = snowLoad / groundToRoofConversionFactor;
    return convertSnowLoad(groundSnowLoad, groundSnowLoadParameter, outputParameter, loadSettings);
  }

  if (outputParameter !== 'Roof') throw new Error('Expected roof snow parameter.');
  const groundSnowLoad = convertSnowLoad(snowLoad, inputParameter, groundSnowLoadParameter, loadSettings);
  const roofSnowLoad = groundToRoofConversionFactor * groundSnowLoad;
  return roofSnowLoad;
}

export function convertSnowLoadFromSettings(loadSettings: LoadSettings, outputParameter: ActiveSnowLoad) {
  return convertSnowLoad(loadSettings.snowLoad, loadSettings.activeSnowLoad, outputParameter, loadSettings);
}

/**
 * This is the client-side implementation of `SnowLoadSettings.GetExposureFactor`
 */
function getExposureFactor(terrainExposureCategory: TerrainExposureCategory, exposureCategory: Exposure) {
  switch (terrainExposureCategory) {
    case 'B_SuburbanAreas':
      switch (exposureCategory) {
        case 'FullyExposed':
          return 0.9;
        case 'PartiallyExposed':
          return 1.0;
        case 'Sheltered':
          return 1.2;
        default:
          assertExhaustiveSwitch(exposureCategory);
      }
    case 'C_ScatteredObstructions':
      switch (exposureCategory) {
        case 'FullyExposed':
          return 0.9;
        case 'PartiallyExposed':
          return 1.0;
        case 'Sheltered':
          return 1.1;
        default:
          assertExhaustiveSwitch(exposureCategory);
      }
    case 'D_FlatOpenTerrain':
      switch (exposureCategory) {
        case 'FullyExposed':
          return 0.8;
        case 'PartiallyExposed':
          return 0.9;
        case 'Sheltered':
          return 1.0;
        default:
          assertExhaustiveSwitch(exposureCategory);
      }
    default:
      assertExhaustiveSwitch(terrainExposureCategory);
  }
}

const rValueTolerance = 0.05;
function rValueEquals(left: number, right: number) {
  return equalWithinTolerance(left, right, rValueTolerance);
}
function rValueGreaterThan(left: number, right: number) {
  return left > right && !rValueEquals(left, right);
}

// Following is the client-side implementation of SnowLoadSettings.GetThermalFactorFromRValue.

const thermalFactorRValueTableLoads = [10, 20, 30, 40, 50, 60, 70] as const;
type ThermalFactorRValueTableLoad = (typeof thermalFactorRValueTableLoads)[number];

type ThermalFactorRValueRow = readonly [
  rValue: number,
  at10: number,
  at20: number,
  at30: number,
  at40: number,
  at50: number,
  at60: number,
  at70: number,
];
const thermalFactorRValueRows: readonly ThermalFactorRValueRow[] = [
  [20, 1.2, 1.11, 1.05, 1.01, 1.0, 1.0, 1.0],
  [30, 1.2, 1.17, 1.14, 1.13, 1.12, 1.11, 1.1],
  [40, 1.2, 1.19, 1.17, 1.16, 1.16, 1.15, 1.15],
  [50, 1.2, 1.2, 1.19, 1.19, 1.19, 1.18, 1.18],
];
type ThermalFactorsInRow = { readonly [K in ThermalFactorRValueTableLoad]: number };
type ThermalFactorRValueRowData = {
  readonly rValue: number;
  readonly thermalFactors: ThermalFactorsInRow;
};
const thermalFactorsRValueTableData: readonly ThermalFactorRValueRowData[] = thermalFactorRValueRows.map(
  ([rValue, ...thermalFactors]) => ({
    rValue: rValue,
    thermalFactors: zipObject(thermalFactorRValueTableLoads, thermalFactors) as ThermalFactorsInRow,
  }),
);

function getThermalFactorFromRValueRow(thermalFactors: ThermalFactorsInRow, groundSnowLoad: number) {
  const previousLoad = thermalFactorRValueTableLoads.findLast((tableLoad) => tableLoad <= groundSnowLoad);
  const nextLoad = thermalFactorRValueTableLoads.find((tableLoad) => tableLoad >= groundSnowLoad);
  return previousLoad == null || nextLoad == null || previousLoad === nextLoad
    ? thermalFactors[previousLoad ?? nextLoad!]
    : interpolate(
        (groundSnowLoad - previousLoad) / (nextLoad - previousLoad),
        thermalFactors[previousLoad],
        thermalFactors[nextLoad],
      );
}

// This is a partial client-side implementation of Polynomial.GetRoots.
/** Solves {@link quadratic}*x^2 + {@link linear}*x + {@link constant} = 0 for x in the interval
 * [{@link start}, {@link end}], assuming {@link quadratic} or {@link linear} is nonzero. */
function solveQuadraticOrLinearEquation(
  constant: number,
  linear: number,
  quadratic: number,
  start: number,
  end: number,
) {
  let roots: readonly number[];
  if (quadratic === 0) roots = [-constant / linear];
  else {
    const a = quadratic;
    const b = linear;
    const c = constant;

    const discriminant = b * b - 4 * a * c;
    if (discriminant <= 0) return [];

    const squareRootOfDiscriminant = Math.sqrt(discriminant);
    roots =
      b >= 0
        ? [(-b - squareRootOfDiscriminant) / (2 * a), (2 * c) / (-b - squareRootOfDiscriminant)]
        : [(2 * c) / (-b + squareRootOfDiscriminant), (-b + squareRootOfDiscriminant) / (2 * a)];
  }

  return roots.filter((root) => root >= start && root <= end);
}

function solveForThermalFactor(thermalFactors: ThermalFactorsInRow, groundSnowLoadTimesThermalFactor: number) {
  const lastLoad = thermalFactorRValueTableLoads.at(-1)!;
  const lastThermalFactor = thermalFactors[lastLoad];
  if (lastThermalFactor * lastLoad <= groundSnowLoadTimesThermalFactor) return lastThermalFactor;

  for (let intervalIndex = thermalFactorRValueTableLoads.length - 2; intervalIndex >= 0; --intervalIndex) {
    const startLoad = thermalFactorRValueTableLoads[intervalIndex];
    const startThermalFactor = thermalFactors[startLoad];
    const endLoad = thermalFactorRValueTableLoads[intervalIndex + 1];
    const endThermalFactor = thermalFactors[endLoad];

    const a = startThermalFactor - endThermalFactor;
    const b = endThermalFactor * startLoad - startThermalFactor * endLoad;
    const c = (endLoad - startLoad) * groundSnowLoadTimesThermalFactor;
    const loadSolutions = solveQuadraticOrLinearEquation(c, b, a, startLoad, endLoad);
    if (loadSolutions.length) return groundSnowLoadTimesThermalFactor / Math.max(...loadSolutions);

    if (startThermalFactor * startLoad <= groundSnowLoadTimesThermalFactor) {
      const midpointThermalFactor = (startThermalFactor + endThermalFactor) / 2;
      const midpointLoad = (startLoad + endLoad) / 2;
      return midpointThermalFactor * midpointLoad <= groundSnowLoadTimesThermalFactor
        ? endThermalFactor
        : startThermalFactor;
    }
  }

  return thermalFactors[thermalFactorRValueTableLoads[0]];
}

function getThermalFactorFromRValue(loadSettings: LoadSettings) {
  const { rValue } = loadSettings;

  if (rValueGreaterThan(rValue, 50)) return 1.2;

  const previousRow = thermalFactorsRValueTableData.findLast((row) => row.rValue <= rValue);
  const nextRow = thermalFactorsRValueTableData.find((row) => row.rValue >= rValue);

  let interpolatedRow: ThermalFactorsInRow;
  if (previousRow == null || nextRow == null || previousRow === nextRow)
    interpolatedRow = (previousRow ?? nextRow!).thermalFactors;
  else {
    const interpolationParameter = (rValue - previousRow.rValue) / (nextRow.rValue - previousRow.rValue);
    interpolatedRow = mapValues(previousRow.thermalFactors, (value, key) =>
      interpolate(interpolationParameter, value, nextRow.thermalFactors[key]),
    );
  }

  if (loadSettings.activeSnowLoad !== 'Roof') {
    const groundSnowLoad = convertSnowLoadFromSettings(loadSettings, 'UltimateGround');
    return getThermalFactorFromRValueRow(interpolatedRow, groundSnowLoad);
  }

  const groundSnowLoadTimesThermalFactor = convertSnowLoad(
    loadSettings.snowLoad,
    loadSettings.activeSnowLoad,
    'UltimateGround',
    loadSettings,
    1,
  );
  return solveForThermalFactor(interpolatedRow, groundSnowLoadTimesThermalFactor);
}

// Following is the client-side implementation of SnowLoadSettings.GetThermalFactor.

export function getThermalFactorOrExtractor(
  thermalCondition: ThermalCondition,
  asce7Version: Asce7Version | null,
): number | ((loadSettings: LoadSettings) => number) {
  switch (thermalCondition) {
    case 'Other':
      return asce7Version == null || !isAsce7_22OrLater(asce7Version) ? 1.0 : getThermalFactorFromRValue;
    case 'ColdVented':
      return asce7Version == null || !isAsce7_22OrLater(asce7Version) ? 1.1 : 1.2;
    case 'Unheated':
      return 1.2;
    case 'FreezerBuilding':
      return 1.3;
    case 'Greenhouse':
      return 0.85;
    default:
      assertExhaustiveSwitch(thermalCondition);
  }
}

export function getThermalFactor(loadSettings: LoadSettings) {
  const thermalFactorOrExtractor = getThermalFactorOrExtractor(
    loadSettings.thermalCondition,
    getAsce7Version(loadSettings.buildingCode),
  );
  if (typeof thermalFactorOrExtractor === 'number') return thermalFactorOrExtractor;
  return loadSettings.manualThermalFactor ?? thermalFactorOrExtractor(loadSettings);
}

/**
 * This is the client-side implementation of `SnowLoadSettings.GetImportanceFactor`
 */
function getImportanceFactor(riskCategory: RiskCategory, asce7Version: Asce7Version | null) {
  if (asce7Version != null && isAsce7_22OrLater(asce7Version)) return 1;
  switch (riskCategory) {
    case 'I':
      return 0.8;
    case 'II':
      return 1.0;
    case 'III':
      return 1.1;
    case 'IV':
      return 1.2;
    default:
      assertExhaustiveSwitch(riskCategory);
  }
}

function rValueGreaterThanOrEqual(left: number, right: number) {
  return left >= right || rValueEquals(left, right);
}

/**
 * This is the client-side implementation of `SnowLoadSettings.GetRoofSlopeFactor`
 */
function getRoofSlopeFactor(
  pitchAngle: number,
  C_t: number,
  isUnobstructedSlipperySurface: boolean,
  isVentilated: boolean,
  rValue: number,
  asce7Version: Asce7Version | null,
) {
  const theta = pitchAngle;
  let startingAngle: number;
  const endingAngle = 70;
  // Slope is so large we don't consider the load.
  if (theta > endingAngle) return 0;

  const isCaseA = asce7Version != null && isAsce7_22OrLater(asce7Version) ? C_t < 1.105 : C_t <= 1.0;

  if (isCaseA) {
    if (
      isUnobstructedSlipperySurface &&
      (asce7Version == null || !isAsce7_22OrLater(asce7Version)) &&
      C_t > 0.85 &&
      rValueGreaterThanOrEqual(rValue, isVentilated ? 20 : 30)
    )
      startingAngle = 5;
    else startingAngle = 30;
  }
  // Colder roof
  else if (C_t < 1.195) {
    if (isUnobstructedSlipperySurface) startingAngle = 10;
    else startingAngle = 37.5;
  }
  // Coldest roofs
  else if (isUnobstructedSlipperySurface) startingAngle = 15;
  else startingAngle = 45;

  if (theta < startingAngle) return 1;

  const C_s = 1 - (theta - startingAngle) / (endingAngle - startingAngle);
  return C_s;
}
